// SCSS Imports
import "./VideoPlayerData.scss";

// React Imports
import React, { useEffect, useState } from "react";
import { API, graphqlOperation } from "aws-amplify";
import {
    EmailShareButton,
    FacebookShareButton,
    TwitterShareButton,
    EmailIcon,
    FacebookIcon,
    TwitterIcon,
} from "react-share";
import Modal from 'react-modal';

// Component Imports
import { createUserData, updateUserData } from "../graphql/mutations";
import { listUserData } from "../graphql/queries";
import CognitoData from "./CognitoData";
import VideoPlayer from "../pages/VideoPlayer";
import Tools from "./Tools";

// Font Awesome Icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faHeart,
    faEnvelope,
    faE,
    faLink,
} from "@fortawesome/free-solid-svg-icons";
import { faFacebook, faTwitter } from "@fortawesome/free-brands-svg-icons";

// Share Buttons
const MyFacebookShareButton = ({ url }) => {
    return (
        <FacebookShareButton
            className="social-button"
            resetButtonStyle={false}
            url={url}
            quote="Check Out this Video from Brainbased Wellness"
        >
            <FontAwesomeIcon className="social-icon" icon={faFacebook} />
            <span className="social-text">Share on Facebook</span>
        </FacebookShareButton>
    );
};
const MyTwitterShareButton = ({ url }) => {
    return (
        <TwitterShareButton
            className="social-button"
            resetButtonStyle={false}
            url={url}
            title="Check Out this Video from Brainbased Wellness"
        >
            <FontAwesomeIcon className="social-icon" icon={faTwitter} />
            <span className="social-text">Share on Twitter</span>
        </TwitterShareButton>
    );
};

const MyEmailShareButton = ({ url }) => {
    return (
        <EmailShareButton
            className="social-button"
            resetButtonStyle={false}
            url={url}
            subject="Check Out this Video from Brainbased Wellness"
            body={url}
        >
            <FontAwesomeIcon className="social-icon" icon={faEnvelope} />
            <span className="social-text">Share with Email</span>
        </EmailShareButton>
    );
};

// Define customStyles before the component
const customStyles = {
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 9999,
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
    },
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: '#1a1a1a',
        border: 'none',
        padding: 0,
        width: '100%',
        maxWidth: '1200px',
        maxHeight: '90vh',
        overflow: 'hidden',
        position: 'relative'
    },
};

const VideoPlayerData = ({
    video,
    instructor,
    isFavorite,
    favorites,
    onFavoriteToggle,
    isOpen,
    onClose,
    children
}) => {
    const [favoriteStatus, setFavoriteStatus] = useState(
        favorites ? favorites.includes(video?.id) : isFavorite
    );
    const [isToolsVisible, setIsToolsVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Add useEffect to handle loading state
    useEffect(() => {
        if (video && instructor) {
            setIsLoading(false);
        }
    }, [video, instructor]);

    // Add useEffect for body scroll locking
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    // Early return if data isn't ready
    if (isLoading) {
        return (
            <Modal
                isOpen={isOpen}
                onRequestClose={onClose}
                style={customStyles}
                contentLabel="Video Player Loading"
            >
                <div className="loading-container">
                    <div>Loading video data...</div>
                </div>
            </Modal>
        );
    }

    // Safety check for required props
    if (!video || !instructor) {
        console.error("Missing required props:", { video, instructor });
        return null;
    }

    const handleFavoriteClick = (e) => {
        try {
            setFavoriteStatus(!favoriteStatus);
            onFavoriteToggle(video.id, !isFavorite);
        } catch (error) {
            console.log("Error updating favorite status:", error);
        }
    };

    const handleShareClick = () => {
        const shareUrl = `${window.location.origin}/play/${video?.id}`;

        if (navigator.clipboard) {
            navigator.clipboard
                .writeText(shareUrl)
                .then(() => {
                    alert("Share link copied to clipboard!");
                })
                .catch((err) => {
                    fallbackCopyTextToClipboard(shareUrl);
                });
        } else {
            fallbackCopyTextToClipboard(shareUrl);
        }
    };

    const fallbackCopyTextToClipboard = (text) => {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            const successful = document.execCommand("copy");
            const msg = successful
                ? "Share link copied to clipboard!"
                : `Share link: ${text}`;
            alert(msg);
        } catch (err) {
            alert(`Share link: ${text}`);
        }

        document.body.removeChild(textArea);
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            style={customStyles}
            contentLabel="Video Player"
        >
            <button className="close-button" onClick={onClose}>
                ×
            </button>
            <div className="modal-scroll-content">
                <div className="video-data-container">
                    <div className="video-data">
                        <div className="video-title-instructor">
                            <h1 className="video-title">{video?.title}</h1>
                            <h3 className="video-instructor">
                                Instructor: {instructor}
                            </h3>
                        </div>
                        <p className="video-description">{video?.description}</p>
                        <div className="video-social-buttons">
                            <button
                                className="social-button"
                                onClick={handleFavoriteClick}
                            >
                                <FontAwesomeIcon
                                    className={`social-icon ${
                                        favoriteStatus ? "favorite" : ""
                                    }`}
                                    icon={faHeart}
                                />
                                <span className="social-text">
                                    {favoriteStatus
                                        ? "Remove Favorite"
                                        : "Add Favorite"}
                                </span>
                            </button>
                            <button
                                className="social-button"
                                onClick={handleShareClick}
                            >
                                <FontAwesomeIcon
                                    className="social-icon"
                                    icon={faLink}
                                />
                                <span className="social-text">Copy Link</span>
                            </button>
                        </div>
                    </div>
                </div>
                {children}
                <div className="video-tools-section">
                    <button
                        className="tools-toggle-button"
                        onClick={() => setIsToolsVisible(!isToolsVisible)}
                    >
                        {isToolsVisible ? 'Hide Tools' : 'Show Tools'}
                    </button>
                    
                    {isToolsVisible && (
                        <div className="tools-content">
                            <Tools />
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default VideoPlayerData;
